import { GraphDetails, GraphTagDto } from "@features/builder/types"
import { addDays, format, isValid, parse, parseISO } from "date-fns"
import { MetadataFormValues } from "@features/builder/UpdateMetadata/validation"
import { DefaultValues } from "react-hook-form"

// BE stores one entry per tag value, form groups them so a tag can hold multiple values
export const groupGraphTagDtosByName = (
  graphTags: GraphTagDto[],
): NonNullable<MetadataFormValues["tags"]> => {
  const tagsByName = new Map<string, NonNullable<MetadataFormValues["tags"]>[number]>()

  graphTags.forEach((tag) => {
    const existing = tagsByName.get(tag.Name)
    if (existing) {
      if (tag.Value) existing.Values.push(tag.Value)
    } else {
      tagsByName.set(tag.Name, {
        Name: tag.Name,
        Rule: tag.Rule,
        Description: tag.Description,
        Values: tag.Value ? [tag.Value] : [],
      })
    }
  })

  return Array.from(tagsByName.values())
}

export const flattenGraphTagFormValues = (
  tags: NonNullable<MetadataFormValues["tags"]>,
): GraphTagDto[] =>
  tags.flatMap((tag) =>
    // keep tags with no values set so they're still saved on the graph, with an empty value
    (tag.Values.length > 0 ? tag.Values : [""]).map((Value) => ({
      Name: tag.Name,
      Rule: tag.Rule,
      Description: tag.Description,
      Value,
    })),
  )

// BE stores scenario descriptions as a map keyed by scenario name, form uses an array so it can be edited via useFieldArray
export const mapScenarioDescriptionsToFormValues = (
  scenarioDescription?: GraphDetails["ScenarioDescription"],
): NonNullable<MetadataFormValues["scenario_descriptions"]> =>
  Object.entries(scenarioDescription ?? {}).map(([Name, value]) => ({
    Name,
    Description: value?.description ?? "",
  }))

export const flattenScenarioDescriptionFormValues = (
  scenarioDescriptions: NonNullable<MetadataFormValues["scenario_descriptions"]>,
): NonNullable<GraphDetails["ScenarioDescription"]> =>
  scenarioDescriptions.reduce<NonNullable<GraphDetails["ScenarioDescription"]>>(
    (acc, { Name, Description }) => {
      acc[Name] = { description: Description ?? "" }
      return acc
    },
    {},
  )

export const getDefaultAndSavedValues = (
  graph?: GraphDetails,
): DefaultValues<MetadataFormValues> => {
  let startDate: string | Date | undefined = graph?.StartDate ?? undefined
  if (startDate && startDate !== "rolling") startDate = parseISO(new Date(startDate).toISOString())
  let endDate: string | Date | undefined = graph?.EndDate ?? undefined
  if (endDate && endDate !== "rolling") endDate = parseISO(new Date(endDate).toISOString())
  return {
    name: graph?.Graph || "",
    description: graph?.description || "",
    product_key: graph?.Product || "",
    start_date: startDate,
    end_date: endDate,
    simulation_frequency: graph?.SimulationFrequency ?? "",
    yearly_rolling_start_month:
      (graph?.YearlyRollingStartMonth && isNaN(+graph.YearlyRollingStartMonth)) ||
      !graph?.YearlyRollingStartMonth
        ? undefined
        : Number(graph?.YearlyRollingStartMonth),
    rolling_duration:
      (graph?.RollingHorizon && isNaN(+graph.RollingHorizon)) || !graph?.RollingHorizon
        ? undefined
        : Number(graph?.RollingHorizon),
    scenario_source: graph?.ScenarioSource,
    scenario_descriptions: mapScenarioDescriptionsToFormValues(graph?.ScenarioDescription),
    owner_group: "", // does not exist right now
    tags: groupGraphTagDtosByName(graph?.GraphTags ?? []),
    overview: graph?.Overview || "",
  }
}

export const formatFormDate = (
  date: MetadataFormValues["end_date"] | MetadataFormValues["start_date"],
): GraphDetails["EndDate"] | GraphDetails["StartDate"] => {
  if (typeof date === "string") {
    if (date === "rolling") {
      return date
    }

    const isoDate = parseISO(date)

    if (isValid(isoDate)) {
      return format(isoDate, "yyyy-MM-dd")
    }

    const slashDate = parse(date, "yyyy/MM/dd", new Date())

    return isValid(slashDate) ? format(slashDate, "yyyy-MM-dd") : date
  }

  if (date instanceof Date) return format(date, "yyyy-MM-dd")

  return null
}

export const getDatePickerDate = (
  dateValue: MetadataFormValues["start_date"] | MetadataFormValues["end_date"],
) => {
  if (!dateValue || dateValue === "rolling") {
    return undefined
  }

  if (dateValue instanceof Date) {
    return isValid(dateValue) ? dateValue : undefined
  }

  const isoDate = parseISO(dateValue)

  if (isValid(isoDate)) {
    return isoDate
  }

  const slashDate = parse(dateValue, "yyyy/MM/dd", new Date())

  return isValid(slashDate) ? slashDate : undefined
}

export const getEndDateMinDate = (startDate: MetadataFormValues["start_date"]) => {
  if (startDate === "rolling") {
    return addDays(new Date(), 1)
  }

  const parsedStartDate = getDatePickerDate(startDate)

  return parsedStartDate ? addDays(parsedStartDate, 1) : undefined
}

export const METADATA_FORM_SECTIONS = {
  details: "metadata-form-section-details",
  links: "metadata-form-section-links",
  scenarios: "metadata-form-section-scenarios",
  tags: "metadata-form-section-tags",
  overview: "metadata-form-section-overview",
} as const

export const METADATA_FORM_SECTION_IDS = Object.values(METADATA_FORM_SECTIONS)
